import React, { useState } from "react";
import { Link } from "react-router-dom";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { db } from "../../firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import emailjs from "@emailjs/browser";

const WaitlistPage = ({ role = "creator" }) => {
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailStatus, setEmailStatus] = useState("pending"); // pending, sent, failed

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!instagram) {
      newErrors.instagram = "Instagram link is required";
    } else {
      const instaRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?$/;
      if (!instaRegex.test(instagram.trim())) {
        newErrors.instagram = "Please enter a valid Instagram profile link";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setErrors({});
    setEmailStatus("pending");

    try {
      console.log("Waitlist: Starting submission process...");
      
      // 1. Save to Firebase Firestore
      console.log("Waitlist: Attempting to save to Firestore (Collection: 'waitlist')...");
      const docRef = await addDoc(collection(db, "waitlist"), {
        email: email.trim(),
        instagram: instagram.trim(),
        role: role,
        joinedAt: serverTimestamp(),
      });
      console.log("Waitlist: Firestore success! Document ID:", docRef.id);
      
      // 2. Show Success Screen Immediately
      setIsSubmitted(true);

      // 3. Send Automated Email in the background
      const SERVICE_ID = "service_8rfmwbh";
      const TEMPLATE_ID = "template_h273c04";
      const PUBLIC_KEY = "8RG71O3v7u90LXqJx";

      if (SERVICE_ID && SERVICE_ID !== "YOUR_SERVICE_ID") {
        console.log("Waitlist: Attempting to send welcome email via EmailJS...");
        try {
          emailjs.init(PUBLIC_KEY);
          const templateParams = {
            email: email.trim(),
            user_email: email.trim(),
            to_email: email.trim(),
            role: role,
            instagram: instagram,
          };

          const result = await emailjs.send(
            SERVICE_ID,
            TEMPLATE_ID,
            templateParams,
            PUBLIC_KEY
          );
          console.log("Waitlist: EmailJS success!", result);
          setEmailStatus("sent");
        } catch (emailErr) {
          console.error("Waitlist: EmailJS background failed:", emailErr);
          setEmailStatus("failed");
        }
      }
    } catch (error) {
      console.error("Waitlist: Submission failed:", error);
      let errorMsg = "Failed to join waitlist. ";
      
      if (error.code === 'permission-denied') {
        errorMsg += "Database permission denied. Please check your Firestore rules.";
      } else {
        errorMsg += error.message || "Please check your internet and try again.";
      }
      
      setErrors({ submit: errorMsg });
    } finally {
      console.log("Waitlist: Submission process complete.");
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#fffcf8] flex items-center justify-center p-4 md:p-6">
        <div className="max-w-md w-full bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-2xl p-8 md:p-10 text-center border border-blue-50 animate-in zoom-in duration-500">
          <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center text-4xl mb-6 mx-auto shadow-inner">
            <i className="fa-solid fa-check"></i>
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 mb-4 tracking-tight">Congratulations!</h1>
          <p className="text-slate-600 leading-relaxed font-medium mb-6">
            You'll be among the first to access this incredible app soon. Keep checking your email for the latest updates from us.
          </p>
          
          {emailStatus === "failed" && (
            <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100 text-amber-700 text-sm font-medium">
              <i className="fa-solid fa-circle-exclamation mr-2"></i>
              Waitlist joined, but we couldn't send the confirmation email. Please check your EmailJS dashboard for errors.
            </div>
          )}

          {emailStatus === "sent" && (
            <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100 text-green-700 text-sm font-medium">
              <i className="fa-solid fa-envelope-circle-check mr-2"></i>
              Confirmation email sent! Check your inbox (and spam).
            </div>
          )}
          <div className="mt-10">
            <Link to="/" className="text-blue-600 font-bold hover:underline">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffcf8] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/50 rounded-full blur-[100px] -mr-48 -mt-48 transition-all"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-100/30 rounded-full blur-[100px] -ml-48 -mb-48"></div>

      <div className="max-w-xl w-full relative z-10">
        <div className="text-center mb-8 md:mb-12">
          <Link to="/" className="flex items-center justify-center gap-2 text-black font-extrabold text-2xl mb-8">
            <img src="/logo.png" alt="Braccet" className="w-10 h-10 object-contain" />
            <span>Braccet</span>
          </Link>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight leading-tight">
            Join the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">{role === "creator" ? "Creator" : "Brand"}</span> Waitlist
          </h1>
          <p className="text-slate-500 font-medium text-base md:text-lg">
            Be the first to experience the future of {role === "creator" ? "creator earnings" : "high-performance marketing"}.
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] md:rounded-[3rem] shadow-2xl shadow-blue-500/10 p-6 md:p-12 border border-white/50">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1 italic">Instagram Profile</label>
              <Input
                name="instagram"
                value={instagram}
                onChange={(e) => {
                  setInstagram(e.target.value);
                  if (errors.instagram) setErrors(prev => ({ ...prev, instagram: "" }));
                }}
                placeholder="instagram.com/username"
                error={errors.instagram}
                className="!rounded-2xl h-14 bg-slate-50/50 border-slate-100"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 ml-1 italic">Email Address</label>
              <Input
                name="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
                }}
                placeholder="you@example.com"
                error={errors.email}
                className="!rounded-2xl h-14 bg-slate-50/50 border-slate-100"
              />
            </div>

            {errors.submit && (
              <p className="text-red-500 text-sm font-bold text-center mb-4">{errors.submit}</p>
            )}

            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              className="w-full h-14 !rounded-2xl text-lg font-bold shadow-lg shadow-blue-500/25 bg-blue-600 hover:bg-blue-700 transition-all hover:scale-[1.02] active:scale-[0.98] mt-4"
            >
              Join Waitlist
            </Button>
          </form>
          <p className="text-center text-slate-400 text-xs mt-6 font-medium">
            Join 500+ {role}s already on the exclusive list.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaitlistPage;
